import { useId, useState, type ReactNode } from 'react';
import { ColorPicker } from './ColorPicker';
import { PresetBar } from './PresetBar';
import { Select } from './Select';
import { Slider } from './Slider';
import { Toggle } from './Toggle';
import { useLanguage, pick } from '../lib/language';
import {
  ASPECT_RATIO_OPTIONS,
  CATCHLIGHT_COUNT_PATTERN_OPTIONS,
  CATCHLIGHT_SHAPE_OPTIONS,
  EMOTION_OPTIONS,
  EYELID_SHAPE_OPTIONS,
  GAZE_DIRECTION_OPTIONS,
  IRIS_PATTERN_OPTIONS,
  LASH_CURVE_OPTIONS,
  LIGHTING_KEY_ANGLE_OPTIONS,
  LIGHTING_QUALITY_OPTIONS,
  LIGHTING_TEMPERATURE_OPTIONS,
  PUPIL_SHAPE_OPTIONS,
  REFLECTED_SCENE_PRESET_OPTIONS,
  RESOLUTION_OPTIONS,
  type AspectRatio,
  type CatchlightCountPattern,
  type CatchlightShape,
  type Emotion,
  type EyelidShape,
  type GazeDirection,
  type IrisPattern,
  type LashCurve,
  type LightingKeyAngle,
  type LightingQuality,
  type LightingTemperature,
  type Parameters,
  type PupilShape,
  type ReflectedScenePreset,
  type Resolution,
} from '../types/parameters';

export interface ControlsPanelProps {
  parameters: Parameters;
  onChange: <K extends keyof Parameters>(key: K, value: Parameters[K]) => void;
  /** True while a chat request is in flight (Apply + Generate are disabled). */
  sending: boolean;
  /** "Apply current settings" — synthesises a message and posts to /chat. */
  onApply: () => void;
  /** Generate using current params, current seed + subject_reference. */
  onGenerate: () => void;
  /**
   * Generate using current params with a new random seed, no
   * subject_reference (breaks continuity on purpose).
   */
  onGenerateFresh: () => void;
  /** Generate 4 variations from the current settings. */
  onGenerateVariations: () => void;
  /** True while the user is choosing one of the 4 variations. */
  pickingVariation: boolean;
  /** Visible label on the Apply button. */
  applyLabel?: string;
  /**
   * Whether to start the wrapping accordion expanded. Sections INSIDE
   * the panel are always closed by default — that's the v5 a11y default
   * (less cognitive load, less motion, friendlier to ADHD / vestibular-
   * sensitive users). When the user opens a section, it stays open until
   * they close it.
   */
  defaultOpen?: boolean;
  /**
   * When provided, the panel renders a small "Done" affordance in the
   * outer header so the user can collapse the entire manual surface and
   * return to chat-only mode without losing the panel state.
   */
  onHidePanel?: () => void;
  /** v6 — replace the entire Parameters object (used by the preset bar). */
  setParameters?: (next: Parameters) => void;
  /**
   * v7 — eyedropper state forwarded to the iris_color ColorPicker so the
   * picker knows whether to arm its global click listener. The image
   * element is the current eye preview; the sample handler updates
   * `iris_color` in the parameters via the existing `onChange` callback.
   */
  eyedropperArmed?: boolean;
  eyedropperImage?: HTMLImageElement | null;
  onEyedropSample?: (hex: string) => void;
  onEyedropCancel?: () => void;
}

const SECTION_KEYS = [
  'anatomy',
  'catchlight',
  'reflected',
  'tint',
  'mood',
  'gaze',
  'lighting',
  'technical',
] as const;
type SectionKey = (typeof SECTION_KEYS)[number];

/**
 * The v6 controls panel — exposes EVERY field on `Parameters` plus a
 * five-preset bar at the top. The presets replace the entire
 * `Parameters` object with one click; the user can still fine-tune any
 * individual field afterwards. Every field has a language-aware
 * tooltip explaining what it controls and what changing it does.
 *
 * v5 a11y preserved: outer accordion is closed by default; every inner
 * section is closed by default. Chat is still the primary surface;
 * manual settings are opt-in.
 */
export function ControlsPanel(props: ControlsPanelProps) {
  const {
    parameters,
    onChange,
    sending,
    onApply,
    onGenerate,
    onGenerateFresh,
    onGenerateVariations,
    pickingVariation,
    applyLabel,
    defaultOpen = false,
    onHidePanel,
    setParameters,
  } = props;

  const { lang } = useLanguage();

  const [panelOpen, setPanelOpen] = useState<boolean>(defaultOpen);
  // v5 a11y — every section starts closed. Users opt in to each group
  // they need rather than scrolling through 40 controls on first paint.
  const [openSections, setOpenSections] = useState<Record<SectionKey, boolean>>({
    anatomy: false,
    catchlight: false,
    reflected: false,
    tint: false,
    mood: false,
    gaze: false,
    lighting: false,
    technical: false,
  });

  const cl = parameters.catchlight;
  const rs = parameters.reflected_scene;
  const ct = parameters.color_tint;
  const lr = parameters.limbal_ring;
  const lt = parameters.lighting;

  const toggleSection = (key: SectionKey) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const busy = sending || pickingVariation;

  const manualLabel = pick(lang, {
    plain: 'Manual settings',
    technical: 'Manual reflection settings',
  });
  const applyLabelText = applyLabel ?? pick(lang, {
    plain: 'Apply settings',
    technical: 'Apply to chat',
  });
  const generateLabel = pick(lang, {
    plain: 'Generate',
    technical: 'Generate (deterministic)',
  });
  const generateFreshLabel = pick(lang, {
    plain: 'Generate fresh',
    technical: 'Generate fresh (new seed)',
  });
  const variationsLabel = pick(lang, {
    plain: 'Generate 4 variations',
    technical: 'Explore 4 variations',
  });
  const introText = pick(lang, {
    plain: 'Sliders edit a draft. Choose how to send your settings — apply to keep iterating, generate fresh to start a new line, or explore 4 variations at once.',
    technical:
      'Draft parameters are local until you send them. Apply updates the chat state, Generate re-runs deterministically, Generate fresh breaks the seed chain.',
  });
  const hideLabel = pick(lang, {
    plain: 'Hide',
    technical: 'Close panel',
  });

  return (
    <aside
      aria-label={manualLabel}
      className="flex flex-col gap-3 rounded-xl border border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-900"
    >
      <div className="flex items-center justify-between gap-2 rounded-t-xl px-4 pt-4">
        <button
          type="button"
          onClick={() => setPanelOpen((o) => !o)}
          aria-expanded={panelOpen}
          aria-controls="controls-panel-body"
          className="inline-flex h-11 flex-1 cursor-pointer items-center justify-between gap-3 rounded-md text-left text-sm font-semibold text-neutral-900 hover:bg-neutral-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent dark:text-neutral-50 dark:hover:bg-neutral-800"
        >
          <span>{manualLabel}</span>
          <span
            aria-hidden="true"
            className={[
              'inline-block motion-safe:transition-transform',
              panelOpen ? 'rotate-180' : 'rotate-0',
            ].join(' ')}
          >
            ▾
          </span>
        </button>
        {onHidePanel ? (
          <button
            type="button"
            onClick={onHidePanel}
            aria-label={`${hideLabel} and return to chat-only mode`}
            className="inline-flex h-11 cursor-pointer items-center justify-center rounded-md border border-neutral-300 px-3 text-xs font-medium text-neutral-700 hover:bg-neutral-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-800"
          >
            {hideLabel}
          </button>
        ) : null}
      </div>

      <div
        id="controls-panel-body"
        hidden={!panelOpen}
        className="flex flex-col gap-4 px-4 pb-4"
      >
        {/* v6 — preset bar above the sliders so mum can pick a starting
            look without scrolling through every field. Hidden until the
            panel is opened (matches the v5 a11y default-collapse). */}
        {setParameters ? (
          <PresetBar
            onApply={setParameters}
            disabled={busy}
          />
        ) : null}

        {/* ── Section: Anatomy ────────────────────────────────────── */}
        <Section
          title={pick(lang, {
            plain: 'Anatomy',
            technical: 'Anatomy',
          })}
          subtitle={pick(lang, {
            plain: 'Eye shape, iris, pupil, eyelids and lashes',
            technical: 'Iris, pupil, sclera, eyelid, lash parameters',
          })}
          open={openSections.anatomy}
          onToggle={() => toggleSection('anatomy')}
        >
          <ColorPicker
            label="Iris colour"
            value={parameters.iris_color}
            onChange={(v) => onChange('iris_color', v)}
            description="CSS hex colour of the iris"
            tooltip={{
              plain: 'The colour of the iris (the coloured ring around the pupil). Pick any colour.',
              technical:
                'CSS hex colour of the iris, e.g. "#5a8aa0".',
            }}
            eyedropperOpen={props.eyedropperArmed}
            eyedropperImage={props.eyedropperImage ?? null}
            onEyedropSample={(hex) => {
              props.onEyedropSample?.(hex);
              onChange('iris_color', hex);
            }}
            onEyedropCancel={() => props.onEyedropCancel?.()}
          />
          <Slider
            label="Iris detail"
            value={parameters.iris_detail}
            min={0}
            max={1}
            step={0.01}
            onChange={(v) => onChange('iris_detail', v)}
            description="Fibre and crypt texture complexity (0 = smooth, 1 = heavily textured)"
            tooltip={{
              plain: 'How much fine texture is visible inside the iris. 0 = flat colour. 1 = every fibre and crypt visible.',
              technical:
                'Fibre and crypt texture complexity (0 = smooth, 1 = heavily textured).',
            }}
          />
          <Select<IrisPattern>
            label="Iris pattern"
            value={parameters.iris_pattern}
            options={IRIS_PATTERN_OPTIONS}
            onChange={(v) => onChange('iris_pattern', v)}
            description="Structural iris type"
            tooltip={{
              plain: 'The overall pattern inside the iris — radiating lines, broken patches, rings, or a mix.',
              technical: 'Structural iris type (radial / cryptic / zonular / mixed).',
            }}
          />

          <Toggle
            label="Show limbal ring"
            checked={lr.enabled}
            onChange={(v) => onChange('limbal_ring', { ...lr, enabled: v })}
            description="The dark annulus at the edge of the iris"
            tooltip={{
              plain: 'The dark ring around the edge of the iris. Almost every real eye has one — leave it on for a realistic look.',
              technical: 'The dark annulus at the edge of the iris (anatomy, default on).',
            }}
          />
          {lr.enabled ? (
            <Slider
              label="Limbal ring intensity"
              value={lr.intensity}
              min={0}
              max={1}
              step={0.01}
              onChange={(v) =>
                onChange('limbal_ring', { ...lr, intensity: v })
              }
              tooltip={{
                plain: 'How dark and thick the limbal ring is. 0 = invisible. 1 = a heavy black ring.',
                technical: '0..1 — strength of the dark annulus at the iris edge.',
              }}
            />
          ) : null}

          <Slider
            label="Pupil size"
            value={parameters.pupil_size}
            min={0}
            max={1}
            step={0.01}
            onChange={(v) => onChange('pupil_size', v)}
            description="0 = constricted (2 mm), 1 = dilated (8 mm)"
            tooltip={{
              plain: 'How big the pupil is. 0 = small (constricted, like in bright light). 1 = large (dilated, like in the dark).',
              technical: '0..1 — 2mm (constricted) to 8mm (dilated).',
            }}
          />
          <Select<PupilShape>
            label="Pupil shape"
            value={parameters.pupil_shape}
            options={PUPIL_SHAPE_OPTIONS}
            onChange={(v) => onChange('pupil_shape', v)}
            tooltip={{
              plain: 'Whether the pupil is perfectly round or slightly irregular.',
              technical: 'Round / slightly-off-round.',
            }}
          />

          <ColorPicker
            label="Sclera tint"
            value={parameters.sclera_tint}
            onChange={(v) => onChange('sclera_tint', v)}
            description="Tinge of the white of the eye (slightly veined, warm / ivory when off-pure)"
            tooltip={{
              plain: 'The colour of the white of the eye. Pure white rarely looks natural — a touch of warm ivory does.',
              technical:
                'CSS hex colour of the sclera, e.g. "#f4ece0". Slightly veined, warm / ivory when off-pure.',
            }}
          />
          <Slider
            label="Sclera veining"
            value={parameters.sclera_veining}
            min={0}
            max={1}
            step={0.01}
            onChange={(v) => onChange('sclera_veining', v)}
            tooltip={{
              plain: 'How visible the tiny red veins in the white of the eye are. 0 = perfectly clean. 1 = bloodshot.',
              technical: '0..1 — visible vein detail in the sclera.',
            }}
          />

          <Select<EyelidShape>
            label="Eyelid shape"
            value={parameters.eyelid_shape}
            options={EYELID_SHAPE_OPTIONS}
            onChange={(v) => onChange('eyelid_shape', v)}
            tooltip={{
              plain: 'How open the eyelid is. Open = alert. Half = relaxed or sleepy. Closed = no eye visible.',
              technical: 'Open / half / closed.',
            }}
          />
          <Slider
            label="Lid shadow"
            value={parameters.lid_shadow}
            min={0}
            max={1}
            step={0.01}
            onChange={(v) => onChange('lid_shadow', v)}
            description="Depth of shadow cast by the lid onto the eye"
            tooltip={{
              plain: 'How dark the shadow is that the eyelid casts onto the eye. More = more drama.',
              technical: '0..1 — depth of shadow cast by the lid onto the eye.',
            }}
          />

          <Slider
            label="Lash density"
            value={parameters.lash_density}
            min={0}
            max={1}
            step={0.01}
            onChange={(v) => onChange('lash_density', v)}
            tooltip={{
              plain: 'How thick and full the eyelashes are. 0 = sparse. 1 = thick and dramatic.',
              technical: '0..1 — lash density.',
            }}
          />
          <Slider
            label="Lash length"
            value={parameters.lash_length}
            min={0}
            max={1}
            step={0.01}
            onChange={(v) => onChange('lash_length', v)}
            tooltip={{
              plain: 'How long the eyelashes are. 0 = short. 1 = sweeping.',
              technical: '0..1 — lash length.',
            }}
          />
          <Select<LashCurve>
            label="Lash curve"
            value={parameters.lash_curve}
            options={LASH_CURVE_OPTIONS}
            onChange={(v) => onChange('lash_curve', v)}
            tooltip={{
              plain: 'How the lashes curve. Straight = natural. Subtle = gently lifted. Dramatic = curled.',
              technical: 'Straight / subtle / dramatic.',
            }}
          />
        </Section>

        {/* ── Section: Catchlight ─────────────────────────────────── */}
        <Section
          title={pick(lang, {
            plain: 'Catchlight',
            technical: 'Catchlight',
          })}
          subtitle={pick(lang, {
            plain: 'The bright reflection on the cornea',
            technical: 'Bright specular reflection on the cornea',
          })}
          open={openSections.catchlight}
          onToggle={() => toggleSection('catchlight')}
        >
          <Toggle
            label="Show catchlight"
            checked={cl.enabled}
            onChange={(v) => onChange('catchlight', { ...cl, enabled: v })}
            tooltip={{
              plain: 'The bright sparkles you see reflected in the eye — what the eye is "looking at".',
              technical: 'Specular highlight on the cornea (default off).',
            }}
          />
          {cl.enabled ? (
            <>
              <Slider
                label="Position left → right"
                value={cl.x}
                min={0}
                max={1}
                step={0.01}
                onChange={(v) => onChange('catchlight', { ...cl, x: v })}
                tooltip={{
                  plain: 'Where the catchlight sits from left to right.',
                  technical: '0..1 horizontal position within the iris.',
                }}
              />
              <Slider
                label="Position top → bottom"
                value={cl.y}
                min={0}
                max={1}
                step={0.01}
                onChange={(v) => onChange('catchlight', { ...cl, y: v })}
                tooltip={{
                  plain: 'Where the catchlight sits from top to bottom.',
                  technical: '0..1 vertical position within the iris.',
                }}
              />
              <Slider
                label="Size"
                value={cl.size}
                min={0.05}
                max={0.6}
                step={0.01}
                onChange={(v) => onChange('catchlight', { ...cl, size: v })}
                tooltip={{
                  plain: 'How big the catchlight is. Bigger catchlights read as a closer light source (window, lamp).',
                  technical: '0..1 size relative to iris radius.',
                }}
              />
              <Select<CatchlightShape>
                label="Shape"
                value={cl.shape}
                options={CATCHLIGHT_SHAPE_OPTIONS}
                onChange={(v) => onChange('catchlight', { ...cl, shape: v })}
                tooltip={{
                  plain: 'The shape of the highlight — round (window), oval (softbox), square, or star (jewellery light).',
                  technical: 'Round / oval / square / star.',
                }}
              />
              <ColorPicker
                label="Catchlight colour"
                value={cl.color}
                onChange={(v) => onChange('catchlight', { ...cl, color: v })}
                tooltip={{
                  plain: 'The tint of the catchlight. Most are white; warm sunsets skew amber, moonlight skews cool blue.',
                  technical: 'CSS hex colour, e.g. "#ffffff".',
                }}
              />
              <Slider
                label="Brightness"
                value={cl.intensity}
                min={0}
                max={1}
                step={0.01}
                onChange={(v) =>
                  onChange('catchlight', { ...cl, intensity: v })
                }
                tooltip={{
                  plain: 'How strong the catchlight is. 0 = barely visible. 1 = blowing out.',
                  technical: '0..1 opacity / brightness.',
                }}
              />
              <Slider
                label="Softness"
                value={cl.blur}
                min={0}
                max={1}
                step={0.01}
                onChange={(v) => onChange('catchlight', { ...cl, blur: v })}
                tooltip={{
                  plain: 'How crisp or fuzzy the catchlight edges are. 0 = sharp pinpoints. 1 = glowing soft.',
                  technical: '0..1 blur radius.',
                }}
              />
              <Slider
                label="Number of catchlights"
                value={cl.count}
                min={1}
                max={3}
                step={1}
                precision={0}
                onChange={(v) =>
                  onChange('catchlight', {
                    ...cl,
                    count: Math.max(1, Math.min(3, Math.round(v))),
                  })
                }
                tooltip={{
                  plain: 'How many separate highlights you see — usually 1 (one light source) or 2 (key + fill).',
                  technical: 'Number of catchlight shapes, 1..3.',
                }}
              />
              <Select<CatchlightCountPattern>
                label="Layout"
                value={cl.count_pattern}
                options={CATCHLIGHT_COUNT_PATTERN_OPTIONS}
                onChange={(v) =>
                  onChange('catchlight', { ...cl, count_pattern: v })
                }
                description="How multiple catchlights are arranged"
                tooltip={{
                  plain: 'How multiple catchlights sit relative to each other — one bright dot, two paired dots, or a cluster.',
                  technical: 'How multiple catchlights are arranged.',
                }}
              />
            </>
          ) : null}
        </Section>

        {/* ── Section: Reflected scene ────────────────────────────── */}
        <Section
          title={pick(lang, {
            plain: 'Reflected scene',
            technical: 'Reflected scene',
          })}
          subtitle={pick(lang, {
            plain: 'A scene mirrored in the eye',
            technical: 'Scene mirrored in the cornea',
          })}
          open={openSections.reflected}
          onToggle={() => toggleSection('reflected')}
        >
          <Toggle
            label="Show reflected scene"
            checked={rs.enabled}
            onChange={(v) =>
              onChange('reflected_scene', { ...rs, enabled: v })
            }
            tooltip={{
              plain: 'Put a tiny scene inside the iris — clouds, fire, forest — like the eye is reflecting its surroundings.',
              technical: 'Overlay a scene in the cornea reflection (default off).',
            }}
          />
          {rs.enabled ? (
            <>
              <Select<ReflectedScenePreset>
                label="Scene preset"
                value={rs.preset}
                options={REFLECTED_SCENE_PRESET_OPTIONS}
                onChange={(v) =>
                  onChange('reflected_scene', { ...rs, preset: v })
                }
                tooltip={{
                  plain: 'Which scene is being reflected in the eye.',
                  technical: 'sky / fire / forest / water / stars / city / abstract.',
                }}
              />
              <Slider
                label="Position left → right"
                value={rs.x}
                min={0}
                max={1}
                step={0.01}
                onChange={(v) => onChange('reflected_scene', { ...rs, x: v })}
                tooltip={{
                  plain: 'Where the reflected scene sits from left to right.',
                  technical: '0..1 horizontal position within the iris.',
                }}
              />
              <Slider
                label="Position top → bottom"
                value={rs.y}
                min={0}
                max={1}
                step={0.01}
                onChange={(v) => onChange('reflected_scene', { ...rs, y: v })}
                tooltip={{
                  plain: 'Where the reflected scene sits from top to bottom.',
                  technical: '0..1 vertical position within the iris.',
                }}
              />
              <Slider
                label="Size"
                value={rs.size}
                min={0.2}
                max={1.4}
                step={0.01}
                onChange={(v) => onChange('reflected_scene', { ...rs, size: v })}
                tooltip={{
                  plain: 'How big the reflected scene is inside the eye.',
                  technical: '0..1 size relative to iris radius.',
                }}
              />
              <Slider
                label="Opacity"
                value={rs.opacity}
                min={0}
                max={1}
                step={0.01}
                onChange={(v) =>
                  onChange('reflected_scene', { ...rs, opacity: v })
                }
                tooltip={{
                  plain: 'How clearly the reflected scene shows through. 0 = invisible. 1 = full opacity.',
                  technical: '0..1 overlay opacity.',
                }}
              />
              <ColorPicker
                label="Tint"
                value={rs.tint}
                onChange={(v) => onChange('reflected_scene', { ...rs, tint: v })}
                tooltip={{
                  plain: 'A colour wash over the reflected scene — warm sunset, cold moonlight, etc.',
                  technical: 'CSS hex tint applied over the scene, e.g. "#88aaff".',
                }}
              />
              <Slider
                label="Rotation"
                value={rs.rotation}
                min={0}
                max={360}
                step={1}
                unit="°"
                precision={0}
                onChange={(v) =>
                  onChange('reflected_scene', { ...rs, rotation: v })
                }
                tooltip={{
                  plain: 'Spin the reflected scene around its centre.',
                  technical: 'Rotation in degrees, 0..360.',
                }}
              />
              <Slider
                label="Distortion (lens curvature)"
                value={rs.distortion}
                min={0}
                max={1}
                step={0.01}
                onChange={(v) =>
                  onChange('reflected_scene', { ...rs, distortion: v })
                }
                tooltip={{
                  plain: 'How curved / fish-eyed the reflection looks. 0 = flat. 1 = heavily bowed by the spherical cornea.',
                  technical: '0..1 — lens-like curvature distortion.',
                }}
              />
            </>
          ) : null}
        </Section>

        {/* ── Section: Colour tint ────────────────────────────────── */}
        <Section
          title={pick(lang, {
            plain: 'Colour tint',
            technical: 'Colour tint',
          })}
          subtitle={pick(lang, {
            plain: 'Overall colour grading pass',
            technical: 'Global colour grading overlay',
          })}
          open={openSections.tint}
          onToggle={() => toggleSection('tint')}
        >
          <Toggle
            label="Apply colour tint"
            checked={ct.enabled}
            onChange={(v) => onChange('color_tint', { ...ct, enabled: v })}
            tooltip={{
              plain: 'Wash the whole eye with a colour — moody purple, sepia, etc.',
              technical: 'Global colour tint pass (default off).',
            }}
          />
          {ct.enabled ? (
            <>
              <ColorPicker
                label="Tint colour"
                value={ct.color}
                onChange={(v) => onChange('color_tint', { ...ct, color: v })}
                tooltip={{
                  plain: 'The colour to wash the image with.',
                  technical: 'CSS hex colour, e.g. "#ffaaff".',
                }}
              />
              <Slider
                label="Strength"
                value={ct.intensity}
                min={0}
                max={1}
                step={0.01}
                onChange={(v) =>
                  onChange('color_tint', { ...ct, intensity: v })
                }
                tooltip={{
                  plain: 'How strong the colour wash is. 0 = none. 1 = fully saturated.',
                  technical: '0..1 mix strength.',
                }}
              />
            </>
          ) : null}
        </Section>

        {/* ── Section: Mood (v5 — emotion field) ───────────────────── */}
        <Section
          title={pick(lang, {
            plain: 'Mood',
            technical: 'Mood',
          })}
          subtitle={pick(lang, {
            plain: 'The feeling the eye is meant to project',
            technical: 'Emotion / mood field (drives catchlight, lid, sclera)',
          })}
          open={openSections.mood}
          onToggle={() => toggleSection('mood')}
        >
          <Select<Emotion>
            label="Emotion"
            value={parameters.emotion}
            options={EMOTION_OPTIONS}
            onChange={(v) => onChange('emotion', v)}
            description="Drives catchlight warmth, lid shape, sclera tension"
            tooltip={{
              plain: 'The feeling the eye should express. Dramatic eyes read as menacing, romantic eyes as soft, etc.',
              technical:
                'Drives catchlight warmth, lid shape, sclera tension (11 enum values).',
            }}
          />
        </Section>

        {/* ── Section: Gaze (v5 — gaze_direction field) ────────────── */}
        <Section
          title={pick(lang, {
            plain: 'Gaze',
            technical: 'Gaze',
          })}
          subtitle={pick(lang, {
            plain: 'Where the eye is looking',
            technical: 'Iris offset / pupil placement direction',
          })}
          open={openSections.gaze}
          onToggle={() => toggleSection('gaze')}
        >
          <Select<GazeDirection>
            label="Gaze direction"
            value={parameters.gaze_direction}
            options={GAZE_DIRECTION_OPTIONS}
            onChange={(v) => onChange('gaze_direction', v)}
            description="Iris offset and pupil placement"
            tooltip={{
              plain: 'Where the eye is looking in the frame.',
              technical:
                'Iris offset and pupil placement (9 enum values including diagonals).',
            }}
          />
        </Section>

        {/* ── Section: Lighting (v5 — lighting block) ─────────────── */}
        <Section
          title={pick(lang, {
            plain: 'Lighting',
            technical: 'Lighting',
          })}
          subtitle={pick(lang, {
            plain: 'Key light direction, quality, and temperature',
            technical: 'Key light direction, quality, temperature',
          })}
          open={openSections.lighting}
          onToggle={() => toggleSection('lighting')}
        >
          <Toggle
            label="Apply lighting setup"
            checked={lt.enabled}
            onChange={(v) => onChange('lighting', { ...lt, enabled: v })}
            tooltip={{
              plain: 'Turn on a directional light hitting the eye. Off by default — anatomy/skin aren\'t affected unless this is on.',
              technical: 'Off by default — anatomy/skin isn\'t affected by lighting unless mum opts in.',
            }}
          />
          {lt.enabled ? (
            <>
              <Select<LightingKeyAngle>
                label="Key angle"
                value={lt.key_angle}
                options={LIGHTING_KEY_ANGLE_OPTIONS}
                onChange={(v) => onChange('lighting', { ...lt, key_angle: v })}
                tooltip={{
                  plain: 'Where the main light is coming from — front, side, above, below.',
                  technical:
                    'front-left / front-right / side-left / side-right / back-left / back-right / above / below / ambient.',
                }}
              />
              <Select<LightingQuality>
                label="Quality"
                value={lt.quality}
                options={LIGHTING_QUALITY_OPTIONS}
                onChange={(v) => onChange('lighting', { ...lt, quality: v })}
                description="Soft = diffused shadows; hard = sharp shadows"
                tooltip={{
                  plain: 'Soft = diffused shadows. Hard = sharp dramatic shadows.',
                  technical: 'Soft / hard.',
                }}
              />
              <Select<LightingTemperature>
                label="Temperature"
                value={lt.temperature}
                options={LIGHTING_TEMPERATURE_OPTIONS}
                onChange={(v) =>
                  onChange('lighting', { ...lt, temperature: v })
                }
                tooltip={{
                  plain: 'Warm = orange / candle. Neutral = daylight. Cool = blue / moonlight.',
                  technical: 'Warm / neutral / cool.',
                }}
              />
              <Slider
                label="Intensity"
                value={lt.intensity}
                min={0}
                max={1}
                step={0.01}
                onChange={(v) => onChange('lighting', { ...lt, intensity: v })}
                description="How dominant the key light is"
                tooltip={{
                  plain: 'How strong the light is. 0 = barely there. 1 = full intensity.',
                  technical: '0..1 — strength of the key light.',
                }}
              />
            </>
          ) : null}
        </Section>

        {/* ── Section: Technical ──────────────────────────────────── */}
        <Section
          title={pick(lang, {
            plain: 'Technical',
            technical: 'Technical',
          })}
          subtitle={pick(lang, {
            plain: 'Aspect ratio, resolution, skin visibility, bokeh',
            technical: 'Aspect ratio, resolution, skin, bokeh',
          })}
          open={openSections.technical}
          onToggle={() => toggleSection('technical')}
        >
          <Select<AspectRatio>
            label="Aspect ratio"
            value={parameters.aspect_ratio}
            options={ASPECT_RATIO_OPTIONS}
            onChange={(v) => onChange('aspect_ratio', v)}
            tooltip={{
              plain: 'The shape of the image — square, landscape, portrait, wide, or tall.',
              technical: '1:1 / 4:3 / 3:4 / 16:9 / 9:16.',
            }}
          />
          <Select<Resolution>
            label="Resolution"
            value={parameters.resolution}
            options={RESOLUTION_OPTIONS}
            onChange={(v) => onChange('resolution', v)}
            tooltip={{
              plain: 'How big the generated image is. Higher = more detail, slower generation.',
              technical: '1K / 2K / 4K (higher = slower).',
            }}
          />
          <Slider
            label="Skin visibility"
            value={parameters.skin_visibility}
            min={0}
            max={1}
            step={0.01}
            onChange={(v) => onChange('skin_visibility', v)}
            description="How much skin surrounds the eye (0 = tight crop, 1 = lots of skin)"
            tooltip={{
              plain: 'How much skin surrounds the eye. 0 = tight crop on the eye. 1 = full face visible.',
              technical: '0..1 — how much skin surrounds the eye.',
            }}
          />
          <Slider
            label="Bokeh strength"
            value={parameters.bokeh_strength}
            min={0}
            max={1}
            step={0.01}
            onChange={(v) => onChange('bokeh_strength', v)}
            description="Skin bokeh intensity"
            tooltip={{
              plain: 'How blurry the surrounding skin is. 0 = sharp everywhere. 1 = heavily blurred (shallow depth-of-field).',
              technical: '0..1 — skin bokeh intensity.',
            }}
          />
        </Section>

        {/* ── Apply + Generate buttons ─────────────────────────────── */}
        <fieldset className="flex flex-col gap-3 rounded-xl border border-neutral-200 bg-neutral-50 p-4 dark:border-neutral-800 dark:bg-neutral-950">
          <legend className="sr-only">Generate with current settings</legend>
          <p className="text-[11px] text-neutral-500 dark:text-neutral-400">
            {introText}
          </p>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              onClick={onApply}
              disabled={busy}
              aria-busy={busy}
              className="inline-flex h-11 cursor-pointer items-center justify-center rounded-md border border-neutral-300 px-4 text-sm font-semibold text-neutral-800 motion-safe:transition-colors hover:bg-neutral-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:cursor-not-allowed disabled:opacity-60 dark:border-neutral-700 dark:text-neutral-100 dark:hover:bg-neutral-800 dark:focus-visible:ring-offset-neutral-950"
            >
              {sending ? pick(lang, { plain: 'Applying…', technical: 'Sending…' }) : applyLabelText}
            </button>
            <button
              type="button"
              onClick={onGenerate}
              disabled={busy}
              aria-busy={busy}
              className="inline-flex h-11 cursor-pointer items-center justify-center rounded-md bg-accent px-5 text-sm font-semibold text-neutral-950 motion-safe:transition-colors hover:bg-accent-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:cursor-not-allowed disabled:opacity-60 dark:focus-visible:ring-offset-neutral-950"
            >
              {sending ? pick(lang, { plain: 'Generating…', technical: 'Generating…' }) : generateLabel}
            </button>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              onClick={onGenerateFresh}
              disabled={busy}
              aria-busy={busy}
              className="inline-flex h-11 cursor-pointer items-center justify-center rounded-md border border-neutral-300 px-4 text-sm font-semibold text-neutral-800 motion-safe:transition-colors hover:bg-neutral-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:cursor-not-allowed disabled:opacity-60 dark:border-neutral-700 dark:text-neutral-100 dark:hover:bg-neutral-800 dark:focus-visible:ring-offset-neutral-950"
            >
              {sending ? pick(lang, { plain: 'Generating…', technical: 'Generating…' }) : generateFreshLabel}
            </button>
            <button
              type="button"
              onClick={onGenerateVariations}
              disabled={busy}
              aria-busy={busy}
              className="inline-flex h-11 cursor-pointer items-center justify-center rounded-md border border-neutral-300 px-4 text-sm font-semibold text-neutral-800 motion-safe:transition-colors hover:bg-neutral-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:cursor-not-allowed disabled:opacity-60 dark:border-neutral-700 dark:text-neutral-100 dark:hover:bg-neutral-800 dark:focus-visible:ring-offset-neutral-950"
            >
              {sending ? pick(lang, { plain: 'Generating…', technical: 'Generating…' }) : variationsLabel}
            </button>
          </div>
        </fieldset>
      </div>
    </aside>
  );
}

/**
 * A single collapsible section inside the controls panel. Wrapped in a
 * `<fieldset>` + `<legend>` so screen readers announce the section
 * grouping and let users navigate by group.
 */
function Section(props: {
  title: string;
  subtitle?: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  const { title, subtitle, open, onToggle, children } = props;
  const bodyId = useId();
  const legendId = useId();
  return (
    <fieldset className="rounded-xl border border-neutral-200 bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-950">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={bodyId}
        className="flex h-11 w-full cursor-pointer items-center justify-between gap-3 rounded-t-xl px-4 text-left text-sm font-semibold text-neutral-900 hover:bg-neutral-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent dark:text-neutral-50 dark:hover:bg-neutral-800"
      >
        <span className="flex flex-col">
          <span id={legendId}>{title}</span>
          {subtitle ? (
            <span className="text-[11px] font-normal text-neutral-500 dark:text-neutral-400">
              {subtitle}
            </span>
          ) : null}
        </span>
        <span
          aria-hidden="true"
          className={[
            'inline-block motion-safe:transition-transform',
            open ? 'rotate-180' : 'rotate-0',
          ].join(' ')}
        >
          ▾
        </span>
      </button>
      <div
        id={bodyId}
        hidden={!open}
        role="group"
        aria-labelledby={legendId}
        className="flex flex-col gap-3 px-4 pb-4 pt-1"
      >
        {children}
      </div>
    </fieldset>
  );
}
